<template>
  <div class="create-voucher bg-white rounded-lg shadow-sm p-6 mb-10">
    <div class="mb-6">
      <h1 class="text-xl font-bold text-neutral-700">Edit Voucher</h1>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Nama Voucher
        </label>
        <TextField v-model="form.name" placeholder="Masukkan nama voucher" />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Kode Voucher
        </label>
        <TextField v-model="form.voucher_code" placeholder="ex: ONGKIR10" />
      </div>

      <div class="md:col-span-2">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Deskripsi
        </label>
        <textarea
          v-model="form.description"
          rows="4"
          maxlength="500"
          placeholder="Masukkan deskripsi voucher..."
          class="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
        ></textarea>
        <p class="mt-1 text-xs text-neutral-500">
          Maksimal 4 baris / 500 karakter
        </p>
      </div>

      <div class="md:col-span-2">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Gambar Voucher
        </label>
        <div
          class="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition"
          @click="$refs.imageInput.click()"
        >
          <input
            ref="imageInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="onImageChange"
          />
          <div v-if="imagePreview" class="flex justify-center">
            <img
              :src="imagePreview"
              alt="Preview"
              class="max-h-40 rounded-md"
            />
          </div>
          <div v-else class="py-4 text-sm text-gray-500">
            Klik untuk upload gambar (PNG/JPG, max 10MB)
          </div>
        </div>
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Layanan Pengiriman <span class="text-gray-400">(Opsional)</span>
        </label>

        <CustomSelect
          :options="deliveryServices"
          :default="selectedDeliveryService"
          optionLabel="name"
          placeholder="Pilih layanan pengiriman"
          @input="(val) => (selectedDeliveryService = val)"
          @change="onDeliveryServiceChange"
        />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Service / Paket <span class="text-gray-400">(Opsional)</span>
        </label>

        <v-select
          multiple
          v-model="selectedPackages"
          :options="packageList"
          label="name"
          placeholder="Pilih service/paket"
          class="style-chooser"
        />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Periode Voucher
        </label>
        <date-range-picker
          v-model="dateRange"
          :locale-data="{ format: 'dd/mm/yyyy HH:MM', separator: ' - ' }"
          :timePicker="true"
          :timePicker24Hour="true"
          :showDropdowns="true"
          :ranges="false"
          class="w-full clean-daterange-picker"
        >
          <template v-slot:input="picker">
            <div
              class="w-full flex justify-between items-center rounded-lg border border-neutral-300 px-3 py-4 bg-white cursor-pointer"
            >
              <span class="text-sm text-neutral-700">
                {{ picker.startDate | date }} - {{ picker.endDate | date }}
              </span>
              <Calendar />
            </div>
          </template>
        </date-range-picker>
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Diskon (%)
        </label>
        <TextField
          type="number"
          v-model="form.discount_percentage"
          placeholder="ex: 10"
        />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Maks Nominal Diskon
        </label>
        <TextField
          type="number"
          v-model="form.max_discount_amount"
          placeholder="ex: 5000"
        />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Kuota
        </label>
        <TextField type="number" v-model="form.quota" placeholder="ex: 100" />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Kuota Tersedia
        </label>
        <TextField
          type="number"
          v-model="form.available_quota"
          placeholder="ex: 50"
        />
      </div>

      <div class="col-span-1">
        <label class="block text-sm font-medium text-neutral-700 mb-2">
          Status
        </label>
        <div class="flex items-center gap-3">
          <Toggle v-model="statusActive" id="voucher-status-toggle" />
          <span class="text-sm font-medium">
            {{ statusActive ? "Active" : "Inactive" }}
          </span>
        </div>
      </div>
    </div>

    <div class="mt-8 flex justify-end gap-3">
      <button
        @click="$router.push('/voucher')"
        class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium transition"
      >
        Batal
      </button>
      <button
        @click="submit"
        :disabled="loading"
        class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium disabled:opacity-50 transition"
      >
        {{ loading ? "Menyimpan..." : "Simpan Voucher" }}
      </button>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs"
import DateRangePicker from "vue2-daterange-picker"
import "vue2-daterange-picker/dist/vue2-daterange-picker.css"
import vSelect from "vue-select"
import "vue-select/dist/vue-select.css"
import { mapActions, mapGetters } from "vuex"

export default {
  name: "EditVoucher",
  components: {
    DateRangePicker,
    vSelect,
    TextField: () => import("@/components/Forms/TextField"),
    CustomSelect: () => import("@/components/Forms/CustomSelect"),
    Toggle: () => import("@/components/Toggle/Toggle"),
    Calendar: () => import("@/components/Icons/Calendar")
  },
  data() {
    return {
      loading: false,
      statusActive: true,
      selectedDeliveryService: null,
      selectedPackages: [],
      imageFile: null,
      imagePreview: null,
      dateRange: {
        startDate: new Date(),
        endDate: new Date()
      },
      form: {
        name: "",
        description: "",
        voucher_code: "",
        discount_percentage: "",
        max_discount_amount: "",
        quota: "",
        available_quota: "",
        delivery_service_id: "",
        status: "Active"
      }
    }
  },
  computed: {
    ...mapGetters("partner", ["deliveryServices", "packageList"]),
    ...mapGetters("voucher", ["voucherDetail"])
  },
  watch: {
    statusActive(val) {
      this.form.status = val ? "Active" : "Inactive"
    }
  },
  filters: {
    date(val) {
      return val ? dayjs(val).format("DD MMM YYYY HH:mm") : ""
    }
  },
  methods: {
    ...mapActions("partner", ["getDeliveryServices", "getPackages"]),
    ...mapActions("voucher", ["getVoucherDetail", "updateVoucher"]),
    ...mapActions("common", ["showExactToast", "showLoading", "hideLoading"]),

    async onDeliveryServiceChange(val) {
      this.form.delivery_service_id = val ? val.id : ""
      this.selectedPackages = []
      if (val && val.id) {
        this.showLoading()
        try {
          await this.getPackages({ id: val.id })
        } finally {
          this.hideLoading()
        }
      }
    },

    onImageChange(e) {
      const file = e.target.files[0]
      if (!file) return
      if (file.size > 10 * 1024 * 1024) {
        this.showExactToast({
          message: "Ukuran gambar maksimal 10MB",
          type: "error"
        })
        return
      }
      this.imageFile = file
      this.imagePreview = URL.createObjectURL(file)
    },

    populateForm() {
      const v = this.voucherDetail
      if (!v) return

      this.form.name = v.name
      this.form.voucher_code = v.voucher_code
      this.form.description = v.description || ""
      this.form.discount_percentage = v.discount_percentage
      this.form.max_discount_amount = v.max_discount_amount || ""
      this.form.quota = v.quota
      this.form.available_quota = v.available_quota
      this.form.status = v.status
      this.statusActive = v.status === "Active"
      this.dateRange.startDate = new Date(v.start_date)
      this.dateRange.endDate = new Date(v.end_date)
      this.imagePreview = v.image_url || null

      if (v.delivery_service) {
        const ds = this.deliveryServices.find(
          (d) => d.id === v.delivery_service.id
        )
        this.selectedDeliveryService = ds || null
        this.form.delivery_service_id = ds ? ds.id : ""

        if (ds) {
          this.showLoading()
          this.getPackages({ id: ds.id })
            .then(() => {
              this.selectedPackages = this.packageList.filter((pkg) =>
                v.service_ids.includes(pkg.id)
              )
            })
            .finally(() => this.hideLoading())
        }
      } else {
        this.selectedDeliveryService = null
        this.selectedPackages = []
        this.form.delivery_service_id = ""
      }
    },

    validate() {
      if (!this.form.name) return "Nama voucher wajib diisi"
      if (!this.form.voucher_code) return "Kode voucher wajib diisi"
      if (!this.form.discount_percentage) return "Diskon wajib diisi"
      if (!this.form.quota) return "Kuota wajib diisi"
      return null
    },

    async submit() {
      const err = this.validate()
      if (err) {
        this.$toast.error(err)
        return
      }

      this.loading = true
      this.showLoading()

      try {
        const formData = new FormData()

        if (this.imageFile) formData.append("image", this.imageFile)
        formData.append("name", this.form.name)
        formData.append("description", this.form.description || "")
        formData.append("voucher_code", this.form.voucher_code)
        formData.append(
          "discount_percentage",
          String(this.form.discount_percentage)
        )
        formData.append("quota", String(this.form.quota))
        formData.append("available_quota", String(this.form.available_quota))
        if (this.form.delivery_service_id) {
          formData.append("delivery_service_id", this.form.delivery_service_id)
        }
        formData.append("status", this.form.status)

        if (this.form.max_discount_amount) {
          formData.append(
            "max_discount_amount",
            String(this.form.max_discount_amount)
          )
        }

        formData.append(
          "start_date",
          dayjs(this.dateRange.startDate).format("YYYY-MM-DD HH:mm:ss")
        )
        formData.append(
          "end_date",
          dayjs(this.dateRange.endDate).format("YYYY-MM-DD HH:mm:ss")
        )

        if (this.selectedPackages.length > 0) {
          this.selectedPackages.forEach((pkg) => {
            formData.append("service_ids", pkg.id)
          })
        }

        await this.updateVoucher({
          id: this.$route.params.id,
          payloads: formData
        })

        this.$toast.success("Voucher berhasil diperbarui")
        this.$router.push("/voucher")
      } catch (err) {
        console.error("UPDATE VOUCHER ERROR:", err)

        this.$toast.error(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            "Gagal memperbarui voucher"
        )
      } finally {
        this.loading = false
        this.hideLoading()
      }
    }
  },
  async created() {
    await this.getDeliveryServices()
    await this.getVoucherDetail({ id: this.$route.params.id })
    this.populateForm()
  }
}
</script>

<style lang="scss" scoped>
::v-deep .vs__dropdown-toggle {
  border-radius: 0.5rem;
  border: 1px solid rgba(206, 213, 222, 1);
  padding: 10px 0;
  background: white;
}

::v-deep .vs__search::placeholder {
  color: #9ca3af;
}

.clean-daterange-picker ::v-deep .form-control {
  border: none;
  box-shadow: none;
  padding: 0;
  height: auto;
  background-color: transparent;
}
</style>
